import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

interface MatenSignProps {
    visible: boolean;
    onHide: () => void;
    onSave: (signatureDataUrl: string, accuracy: number, width: number, height: number) => void;
    title?: string;
    accuracy?: number; // accuracy in percentage (0 to 100)
}

const MatenSign = observer(
    ({ visible, onHide, onSave, title = 'Unterschrift', accuracy }: MatenSignProps) => {
        const signatureRef = useRef<SignatureCanvas>(null);
        const [canvasProps, setCanvasProps] = useState<any>({});
        const [isEmpty, setIsEmpty] = useState(true);
        const [hasMinimumContent, setHasMinimumContent] = useState(false);

        const calculateCurrentAccuracy = () => {
            if (!signatureRef.current) return 0;

            const canvas = signatureRef.current.getCanvas();
            const ctx = canvas.getContext('2d');
            if (!ctx) return 0;

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            let filledPixels = 0;
            const totalPixels = canvas.width * canvas.height;

            // Zähle nicht-weiße Pixel (RGBA: nicht 255,255,255,255)
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                const a = data[i + 3];

                // Pixel ist gefüllt wenn er nicht weiß ist
                if (!(r === 255 && g === 255 && b === 255) || a < 255) {
                    filledPixels++;
                }
            }

            const fillPercentage = (filledPixels / totalPixels) * 100;
            return Math.round(fillPercentage * 100) / 100; // Auf 2 Dezimalstellen runden
        };

        const checkCanvasContent = () => {
            if (!signatureRef.current) return;

            const canvasIsEmpty = signatureRef.current.isEmpty();
            const currentAccuracy = calculateCurrentAccuracy();
            const minimumPercentage = accuracy || 0.5; // default 0.5 to have a minimum signature
            const hasEnoughContent = currentAccuracy >= minimumPercentage;

            setIsEmpty(canvasIsEmpty);
            setHasMinimumContent(hasEnoughContent);
        };

        const getCanvasProps = () => {
            // Verfügbare Dimensionen - mehr Platz nutzen
            const availableWidth = window.innerWidth - 40; // Reduziert von 80px auf 40px
            const availableHeight = window.innerHeight - 180; // Reduziert von 200px auf 180px

            // Orientierung erkennen
            const isLandscape = window.innerWidth > window.innerHeight;

            let cssWidth, cssHeight;

            if (isLandscape) {
                // Im Landscape: Maximale Breite nutzen, aber Höhe begrenzen
                const maxHeight = Math.min(availableHeight, 130); // Erhöht von 120px auf 130px
                const maxWidth = maxHeight * 4.5; // Erhöht von 4:1 auf 4.5:1 für mehr Breite

                cssWidth = Math.min(availableWidth, maxWidth);
                cssHeight = Math.min(cssWidth / 4.5, maxHeight);
            } else {
                // Im Portrait: Breiteres Verhältnis
                const minWidth = 320;
                const maxWidth = 550;

                cssWidth = Math.min(Math.max(availableWidth, minWidth), maxWidth);
                cssHeight = Math.round(cssWidth / 4); // Geändert von 3.5:1 auf 4:1 für mehr Breite

                // Sicherstellen, dass es in die verfügbare Höhe passt
                if (cssHeight > availableHeight) {
                    cssHeight = availableHeight;
                    cssWidth = cssHeight * 4;
                }
            }

            return {
                width: cssWidth,
                height: cssHeight,
                style: {
                    width: `${cssWidth}px`,
                    height: `${cssHeight}px`,
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    touchAction: 'none',
                    background: '#fff',
                },
                className: 'signature-canvas',
            };
        };

        const updateCanvasSize = () => {
            const newProps = getCanvasProps();
            setCanvasProps(newProps);
        };

        useEffect(() => {
            // Canvas-Eigenschaften initial setzen
            updateCanvasSize();

            const handleResize = () => {
                updateCanvasSize();
            };

            const handleOrientationChange = () => {
                // Timeout um sicherzustellen, dass die Orientierung vollständig geändert wurde
                setTimeout(() => {
                    updateCanvasSize();
                }, 150);
            };

            window.addEventListener('resize', handleResize);
            window.addEventListener('orientationchange', handleOrientationChange);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('orientationchange', handleOrientationChange);
            };
            // eslint-disable-next-line
        }, []);

        const handleClear = () => {
            if (signatureRef.current) {
                signatureRef.current.clear();
                setIsEmpty(true);
                setHasMinimumContent(false);
            }
        };

        const handleSave = () => {
            if (signatureRef.current && !isEmpty) {
                const currentAccuracy = calculateCurrentAccuracy();
                const originalDataUrl = signatureRef.current.toDataURL();
                const targetWidth = 350;
                const targetHeight = 100;

                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    if (ctx) {
                        // Weißer Hintergrund
                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, targetWidth, targetHeight);

                        // Signatur skaliert zeichnen
                        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

                        // Skalierte Signatur als DataURL
                        const scaledDataUrl = canvas.toDataURL();
                        onSave(scaledDataUrl, currentAccuracy, targetWidth, targetHeight);
                    }
                };
                img.src = originalDataUrl;

                handleClear();
                onHide();
            }
        };

        const handleBegin = () => {
            setIsEmpty(false);
        };

        const handleEnd = () => {
            // Prüfe Canvas-Inhalt nach dem Zeichnen
            setTimeout(() => {
                checkCanvasContent();
            }, 50); // Kurze Verzögerung für Canvas-Update
        };

        const dialogFooter = (
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <Button
                    label="Löschen"
                    icon="pi pi-times"
                    severity="secondary"
                    onClick={handleClear}
                    disabled={isEmpty}
                    style={{ padding: '0.75rem 1rem' }}
                />
                <Button
                    label="Speichern"
                    icon="pi pi-check"
                    onClick={handleSave}
                    disabled={isEmpty || !hasMinimumContent}
                    style={{ padding: '0.75rem 1rem' }}
                />
            </div>
        );

        return (
            <Dialog
                visible={visible}
                onHide={onHide}
                header={title}
                footer={dialogFooter}
                modal
                draggable={false}
                resizable={false}
                style={{
                    width: 'calc(100vw - 1rem)', // Reduziert von 2rem auf 1rem
                    maxWidth: '800px', // Erhöht von 600px auf 800px
                    maxHeight: '95vh',
                    margin: '0.5rem', // Reduziert von 1rem auf 0.5rem
                }}
                contentStyle={{
                    padding: '0.5rem', // Reduziert von 0.75rem
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem', // Reduziert von 0.75rem
                    overflow: 'visible',
                }}
                headerStyle={{
                    textAlign: 'center',
                    fontWeight: '600',
                    padding: '0.5rem', // Reduziert von 0.75rem
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem', // Reduziert von 0.75rem
                        width: '100%',
                    }}
                >
                    <p
                        style={{
                            margin: 0,
                            fontSize: '0.9rem',
                            color: '#6c757d',
                            textAlign: 'center',
                        }}
                    >
                        Bitte unterschreiben Sie mit dem Finger oder einem Stift
                    </p>

                    <div
                        style={{
                            border: '2px solid #dee2e6',
                            borderRadius: '8px',
                            padding: '0.2rem', // Reduziert von 0.25rem
                            backgroundColor: '#f8f9fa',
                        }}
                    >
                        <SignatureCanvas
                            ref={signatureRef}
                            canvasProps={canvasProps}
                            onBegin={handleBegin}
                            onEnd={handleEnd}
                            penColor="black"
                            backgroundColor="white"
                        />
                    </div>

                    <p
                        style={{
                            margin: 0,
                            fontSize: '0.8rem',
                            color: isEmpty ? '#868e96' : hasMinimumContent ? '#28a745' : '#fd7e14',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            minHeight: '1.2rem', // Feste Höhe verhindert Layout-Sprünge
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {isEmpty
                            ? 'Beginnen Sie zu zeichnen'
                            : hasMinimumContent
                              ? 'Zeichnung bereit zum Speichern'
                              : 'Bitte vervollständigen Sie Ihre Unterschrift'}
                    </p>
                </div>
            </Dialog>
        );
    }
);

export default MatenSign;
